import { SOCIAL_LIMIT_ERROR_CODES, SocialLimitErrorBody, SocialLimitErrorCode } from '@digital-twins/shared-types'
import { TFunction } from 'i18next'

import { formatSocialRelativeTime } from './socialCredits'

/**
 * Typed run-limit errors (production limits Step 3) as toasts: `429
 * social_update_cooldown`, `429 social_queue_full` and `402
 * social_out_of_credits` from update, update-all and resume. Callers (Topic
 * page, card, Activity) show the result with their own toast; the wizard maps
 * a create 402 itself. Anything else returns null, so the caller keeps its
 * own fallback message.
 */

export interface SocialLimitToastAction {
  label: string
  onClick: () => void
}

export interface SocialLimitToast {
  code: SocialLimitErrorCode
  message: string
  action?: SocialLimitToastAction
}

export interface SocialLimitToastOptions {
  /** Owners get "Contact sales"; members are told to ask the owner. */
  isOrgOwner: boolean
  /** `config.contactSalesUrl ?? CONTACT_SALES_URL`, opened in a new tab. */
  contactSalesUrl: string
  /** UI language for "in 40 minutes"; defaults to English. */
  language?: string | null
  /** Clock for the cooldown wording (tests). */
  now?: number
  /** Opens the contact page (tests); defaults to a new tab. */
  openUrl?: (url: string) => void
}

const LIMIT_CODES = new Set<string>(SOCIAL_LIMIT_ERROR_CODES)

function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

/** The typed body of a run-limit error (axios-style `response.data`), or null. */
export function socialLimitErrorBody(error: unknown): SocialLimitErrorBody | null {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data
  if (!data || typeof data !== 'object') {
    return null
  }
  const code = (data as { code?: unknown }).code
  if (typeof code !== 'string' || !LIMIT_CODES.has(code)) {
    return null
  }
  const retryAfter = (data as { retryAfterSeconds?: unknown }).retryAfterSeconds
  return {
    code: code as SocialLimitErrorCode,
    retryAfterSeconds: typeof retryAfter === 'number' && Number.isFinite(retryAfter) ? retryAfter : undefined,
  }
}

/** Toast copy for a run-limit error; null when the error is not one. */
export function socialLimitErrorToast(
  error: unknown,
  t: TFunction<'app'>,
  options: SocialLimitToastOptions,
): SocialLimitToast | null {
  const body = socialLimitErrorBody(error)
  if (!body) {
    return null
  }
  if (body.code === 'social_update_cooldown') {
    const now = options.now ?? Date.now()
    const seconds = Math.max(1, body.retryAfterSeconds ?? 60)
    const when = formatSocialRelativeTime(now + seconds * 1000, options.language, now)
    return { code: body.code, message: t('socialListening.errors.updateCooldown', { when }) }
  }
  if (body.code === 'social_queue_full') {
    return { code: body.code, message: t('socialListening.errors.queueFull') }
  }
  if (!options.isOrgOwner) {
    return { code: body.code, message: t('socialListening.errors.outOfCreditsMember') }
  }
  const openUrl = options.openUrl ?? openInNewTab
  return {
    code: body.code,
    message: t('socialListening.errors.outOfCreditsOwner'),
    action: {
      label: t('billing.contactSales'),
      onClick: () => openUrl(options.contactSalesUrl),
    },
  }
}
